import { useState, useEffect } from 'react';
import { depositsApi, settingsApi } from '../api';
import api from '../api';
import { useAuthStore } from '../store';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Wallet, Upload, Loader2, Copy, Building2, Calculator, AlertCircle, Check } from 'lucide-react';
import { uploadFile } from '../api';

const formatMoney = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount);
const formatDate = (date) => new Date(date).toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

// Calculate deposit fee based on amount ranges
const calculateFee = (amount) => {
  if (amount <= 0) return 0;
  const bracket = Math.ceil(amount / 10000);
  return bracket * 100;
};

export default function DepositsPage() {
  const { user, fetchUser } = useAuthStore();
  const [deposits, setDeposits] = useState([]);
  const [settings, setSettings] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState('');
  const [accountName, setAccountName] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [feeInfo, setFeeInfo] = useState({ fee: 0, credited: 0 });

  useEffect(() => { fetchData(); }, []);

  // Calculate fee when amount changes
  useEffect(() => {
    const amt = parseFloat(amount) || 0;
    const fee = calculateFee(amt);
    setFeeInfo({
      fee: fee,
      credited: amt > 0 ? Math.max(0, amt - fee) : 0
    });
  }, [amount]);

  const fetchData = async () => {
    try {
      const [depositsRes, settingsRes] = await Promise.all([depositsApi.getAll(), settingsApi.get()]);
      setDeposits(depositsRes.data);
      setSettings(settingsRes.data);
      
      // Get bank accounts from settings
      const accounts = settingsRes.data.bank_accounts || [];
      // If no accounts array but legacy fields exist, use them
      if (accounts.length === 0 && settingsRes.data.bank_name) {
        accounts.push({
          bank_name: settingsRes.data.bank_name,
          account_number: settingsRes.data.bank_account_number,
          account_name: settingsRes.data.bank_account_name
        });
      }
      setBankAccounts(accounts);
      // Select first bank by default
      if (accounts.length > 0) {
        setSelectedBank(0);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      setReceiptPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !accountName) { toast.error('Please fill in all required fields'); return; }
    if (selectedBank === null) { toast.error('Please select a bank account'); return; }
    
    const amt = parseFloat(amount);
    const fee = calculateFee(amt);
    if (amt - fee <= 0) {
      toast.error('Deposit amount is too low after fee deduction');
      return;
    }
    
    setSubmitting(true);
    try {
      let receiptUrl = null;
      if (receiptFile) {
        const uploadRes = await uploadFile(receiptFile);
        receiptUrl = uploadRes.url;
      }
      await depositsApi.create({ amount: amt, payment_account_name: accountName, receipt_image: receiptUrl });
      toast.success(`Deposit request submitted! ₦${(amt - fee).toLocaleString()} will be credited after approval.`);
      setAmount(''); setAccountName(''); setReceiptFile(null); setReceiptPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deposit');
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#FFD700]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white font-['Outfit']">Deposits</h1>
        <div className="text-right">
          <p className="text-sm text-zinc-500">Current Balance</p>
          <p className="text-2xl font-bold text-[#FFD700]">{formatMoney(user?.balance || 0)}</p>
        </div>
      </div>

      <Tabs defaultValue="deposit">
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          <TabsTrigger value="deposit">Make Deposit</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="deposit" className="space-y-6 mt-6">
          {/* Fee Info Card */}
          <Card className="bg-gradient-to-r from-[#FFD700]/10 to-transparent border-[#FFD700]/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Calculator className="w-5 h-5 text-[#FFD700] mt-0.5" />
                <div>
                  <p className="text-white font-semibold">Deposit Fee Structure</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    ₦0 - ₦10,000 = ₦100 fee | ₦10,001 - ₦20,000 = ₦200 fee | and so on...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Selection */}
          <Card className="bg-[#0A0A0A] border-[#FFD700]/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#FFD700]" />
                Select Bank Account
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {bankAccounts.length === 0 ? (
                <p className="text-zinc-500">Bank details not configured. Please contact admin.</p>
              ) : bankAccounts.length === 1 ? (
                // Single bank - show directly
                <div className="p-4 bg-[#121212] rounded-lg border-2 border-[#FFD700]/50">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Bank Name</span>
                      <span className="text-white font-semibold">{bankAccounts[0].bank_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Account Number</span>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-mono font-semibold">{bankAccounts[0].account_number}</span>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(bankAccounts[0].account_number)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-zinc-400">Account Name</span>
                      <span className="text-white font-semibold">{bankAccounts[0].account_name}</span>
                    </div>
                  </div>
                </div>
              ) : (
                // Multiple banks - allow selection
                <div className="grid gap-3">
                  {bankAccounts.map((bank, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedBank(index)}
                      className={`p-4 bg-[#121212] rounded-lg border-2 cursor-pointer transition-all ${
                        selectedBank === index 
                          ? 'border-[#FFD700] bg-[#FFD700]/5' 
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-bold">{bank.bank_name}</span>
                            {selectedBank === index && (
                              <Check className="w-4 h-4 text-[#FFD700]" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-zinc-400 text-sm">Account:</span>
                            <span className="text-white font-mono">{bank.account_number}</span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0"
                              onClick={(e) => { e.stopPropagation(); copyToClipboard(bank.account_number); }}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <p className="text-zinc-500 text-sm">{bank.account_name}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#0A0A0A] border-white/10">
            <CardHeader><CardTitle className="text-white">Submit Deposit</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Amount (₦)</Label>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" className="bg-[#121212] border-white/10 text-white" required data-testid="deposit-amount-input" />
                </div>
                
                {/* Fee Breakdown */}
                {parseFloat(amount) > 0 && (
                  <div className="p-4 bg-[#121212] rounded-lg border border-white/10 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Deposit Amount</span>
                      <span className="text-white font-semibold">{formatMoney(parseFloat(amount))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Processing Fee</span>
                      <span className="text-red-400 font-semibold">- {formatMoney(feeInfo.fee)}</span>
                    </div>
                    <div className="border-t border-white/10 pt-2 flex justify-between">
                      <span className="text-zinc-300 font-semibold">You Will Receive</span>
                      <span className="text-[#FFD700] font-bold text-lg">{formatMoney(feeInfo.credited)}</span>
                    </div>
                    {feeInfo.credited <= 0 && (
                      <div className="flex items-center gap-2 text-red-400 text-sm mt-2">
                        <AlertCircle className="w-4 h-4" />
                        <span>Amount too low after fee deduction</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-zinc-300">Your Account Name (Sender)</Label>
                  <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} placeholder="Name on your bank account" className="bg-[#121212] border-white/10 text-white" required data-testid="deposit-account-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Payment Receipt (Optional)</Label>
                  <div className="border-2 border-dashed border-white/10 rounded-lg p-4 text-center">
                    <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="receipt-upload" />
                    <label htmlFor="receipt-upload" className="cursor-pointer">
                      {receiptPreview ? <img src={receiptPreview} alt="Receipt" className="max-h-40 mx-auto rounded" /> : <><Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" /><p className="text-sm text-zinc-500">Click to upload receipt</p></>}
                    </label>
                  </div>
                </div>
                <Button type="submit" disabled={submitting || feeInfo.credited <= 0 || (bankAccounts.length > 1 && selectedBank === null)} className="w-full bg-[#FFD700] text-black hover:bg-[#E5C100] font-bold" data-testid="submit-deposit-btn">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Submitting...</> : `Submit Deposit (${formatMoney(feeInfo.credited)} will be credited)`}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          {deposits.length === 0 ? (
            <div className="text-center py-12"><Wallet className="w-16 h-16 mx-auto text-zinc-600 mb-4" /><p className="text-zinc-400">No deposit history</p></div>
          ) : (
            <div className="space-y-4">
              {deposits.map((deposit) => (
                <Card key={deposit.id} className="bg-[#0A0A0A] border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white">{formatMoney(deposit.amount)}</p>
                        {deposit.fee > 0 && (
                          <p className="text-sm text-zinc-500">
                            Fee: {formatMoney(deposit.fee)} | Credited: <span className="text-[#FFD700]">{formatMoney(deposit.credited_amount)}</span>
                          </p>
                        )}
                        <p className="text-sm text-zinc-500">{formatDate(deposit.created_at)}</p>
                        {deposit.admin_note && <p className="text-sm text-zinc-400 mt-1">Note: {deposit.admin_note}</p>}
                      </div>
                      <Badge variant="outline" className={deposit.status === 'approved' ? 'border-green-500/30 text-green-500' : deposit.status === 'rejected' ? 'border-red-500/30 text-red-500' : 'border-yellow-500/30 text-yellow-500'}>{deposit.status}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
